import React, { useEffect, useRef, useState } from 'react';
import BrandLoader from 'shared/components/ui/BrandLoader';
import AskIcon from 'shared/components/ui/AskIcon';
import { GripVertical, MoreHorizontal } from 'lucide-react';
import ChatInlineChart from 'shared/components/chat/ChatInlineChart';
import AnswerMarkdown from 'shared/components/chat/AnswerMarkdown';
import { AnswerTable, chartSpecFromBlock, formatCell, humanLabel } from 'shared/components/chat/AnswerRenderer';
import { errorText } from 'features/dashboards/format';
import { RichTextView } from 'features/dashboards/components/RichText';

/**
 * One card: run its query, draw the block Ask's composer returned.
 *
 * Every card fetches itself, through one queue that keeps at most six in
 * flight — a browser allows about six connections per origin, and a 34-card
 * Overview must not starve the page's own requests.
 */
const MAX_IN_FLIGHT = 6;
let inFlight = 0;
const waiting = [];
/**
 * Identical requests share one promise while in flight: a remount (React's
 * development double-mount, a dashboard reload after an edit) must not send
 * the same card twice.
 */
const shared = new Map();
/**
 * Finished results are kept for two minutes under the same key, so switching
 * tabs (which remounts the grid) or coming back to a report does not run every
 * card again. The key holds the card, its spec, the window and the filters, so
 * a change to any of them is a different entry.
 */
const RESULT_TTL_MS = 2 * 60 * 1000;
const RESULT_MAX = 300;
const results = new Map();
export function clearCardResults() { results.clear(); }
function once(key, task) {
  const hit = results.get(key);
  if (hit && Date.now() - hit.at < RESULT_TTL_MS) return Promise.resolve(hit.value);
  if (shared.has(key)) return shared.get(key);
  const p = enqueue(task)
    .then((value) => {
      results.set(key, { at: Date.now(), value });
      if (results.size > RESULT_MAX) results.delete(results.keys().next().value);
      return value;
    })
    .finally(() => shared.delete(key));
  shared.set(key, p);
  return p;
}

/** True once the element has come within a screen of the viewport; stays true. */
function useSeen(ref, disabled) {
  const [seen, setSeen] = useState(disabled || typeof IntersectionObserver === 'undefined');
  useEffect(() => {
    if (seen || !ref.current) return undefined;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { setSeen(true); io.disconnect(); }
    }, { rootMargin: '300px 0px' });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [seen, ref]);
  return seen;
}

function enqueue(task) {
  return new Promise((resolve, reject) => {
    const start = () => {
      inFlight += 1;
      task().then(resolve, reject).finally(() => {
        inFlight -= 1;
        const next = waiting.shift();
        if (next) next();
      });
    };
    if (inFlight < MAX_IN_FLIGHT) start(); else waiting.push(start);
  });
}

function Delta({ now, before, unit }) {
  if (now == null || before == null || !Number.isFinite(now) || !Number.isFinite(before)) return null;
  if (before === 0) return <span className="text-[11px] text-[color:var(--text-muted)]">vs {formatCell(before, { unit })} previous</span>;
  // Neutral on purpose: whether up is good depends on the metric — more runs
  // is good news, more cost or failures is not.
  const pct = ((now - before) / Math.abs(before)) * 100;
  return (
    <span className="text-[11px] tabular-nums text-[color:var(--text-muted)]">
      {pct >= 0 ? '▲' : '▼'} {Math.abs(pct).toFixed(Math.abs(pct) < 10 ? 1 : 0)}% vs previous
    </span>
  );
}

function Kpis({ block, previous }) {
  const items = block.items || [];
  return (
    <div className="grid h-full content-center gap-3" style={{ gridTemplateColumns: `repeat(${Math.min(items.length, 4)}, minmax(0, 1fr))` }}>
      {items.map((k) => {
        const before = previous ? previous[k.label] : null;
        return (
          <div key={k.label} className="min-w-0">
            {items.length > 1 ? <div className="truncate text-[11px] text-[color:var(--text-muted)]">{humanLabel(k.label)}</div> : null}
            <div className={`truncate font-semibold tabular-nums text-[color:var(--text-primary)] ${items.length === 1 ? 'text-[30px] leading-tight' : 'text-[22px]'}`}>
              {k.value == null ? '—' : formatCell(k.value, { unit: k.unit })}
            </div>
            <Delta now={k.value} before={before} unit={k.unit} />
          </div>
        );
      })}
    </div>
  );
}

/** The composer's reason, as a person reads it: its first sentence. */
function firstSentence(t) {
  const one = String(t || '').split(/(?<=\.)\s/)[0].replace(/^card /, 'the data ');
  return one.charAt(0).toLowerCase() + one.slice(1);
}

const BUCKET_NAMES = { 3600: 'hour', 86400: 'day', 604800: 'week' };

export function CardBody({ result }) {
  const { block } = result;
  if (!block) return <Center muted>{result.note ? 'Nothing to show for this window' : 'No data in this window'}</Center>;
  if (block.kind === 'kpis') return <Kpis block={block} previous={result.previous} />;
  if (block.kind === 'chart') {
    const spec = chartSpecFromBlock(block);
    if (!spec) return <Center muted>No data in this window</Center>;
    const unit = BUCKET_NAMES[result.bucket_seconds];
    if (!result.partial_from || !unit) return <ChatInlineChart spec={{ ...spec, title: '' }} bare />;
    return (
      <div className="flex h-full flex-col">
        <div className="min-h-0 flex-1"><ChatInlineChart spec={{ ...spec, title: '' }} bare /></div>
        <div className="shrink-0 text-right text-[10px] text-[color:var(--text-muted)]">Last {unit} so far</div>
      </div>
    );
  }
  if (block.kind === 'table') {
    return (
      <div className="flex h-full flex-col">
        <div className="min-h-0 flex-1 overflow-auto"><AnswerTable block={{ ...block, title: null }} entityIndex={new Map()} /></div>
        {result.note ? <div className="shrink-0 pt-1 text-[10px] text-[color:var(--text-muted)]" title={result.note}>Shown as a table: {firstSentence(result.note)}</div> : null}
      </div>
    );
  }
  if (block.kind === 'text') return <div className="h-full overflow-auto text-[13px]"><AnswerMarkdown text={block.markdown || ''} /></div>;
  if (block.kind === 'rich_text') return <div className="h-full overflow-auto"><RichTextView doc={block.doc} /></div>;
  return null;
}

export function Center({ children, muted, error }) {
  return (
    <div className={`flex h-full items-center justify-center px-2 text-center text-[11px] ${error ? 'text-[color:var(--feedback-error)]' : muted ? 'text-[color:var(--text-muted)]' : ''}`}>
      {children}
    </div>
  );
}

/**
 * @param {object} props
 * @param {object} props.card      {id, kind, viz, ...}
 * @param {Function} props.run     (cardId) => Promise<result> — the page decides team, window, filters
 * @param {string} props.runKey    changes when the window or filters change
 * @param {boolean} props.editing
 * @param {Function} [props.onMenu] opens the card's menu (edit mode)
 */
export default function DashboardCard({ card, run, runKey, editing, onMenu, onAsk, onEditText, renaming, onRename, onStartRename }) {
  const [state, setState] = useState({ loading: card.kind !== 'text', result: null, error: null });
  const runRef = useRef(run);
  runRef.current = run;
  // A card below the fold does not run until it is about to be seen.
  const rootRef = useRef(null);
  const seen = useSeen(rootRef, card.kind === 'text');

  // Keyed on what the card asks, not on object identity: a reloaded
  // dashboard hands every card a new spec object with the same contents.
  // The chart type is drawn by the server, so choosing another one re-runs the card.
  const specKey = JSON.stringify([card.kind, card.spec, card.viz?.chart || null, card.viz?.series || null]);
  useEffect(() => {
    if (card.kind === 'text') {
      setState({ loading: false, error: null, result: { block: card.spec?.doc ? { kind: 'rich_text', doc: card.spec.doc } : { kind: 'text', markdown: card.spec?.markdown || '' } } });
      return undefined;
    }
    if (!seen) return undefined;
    let alive = true;
    setState((s) => ({ ...s, loading: true, error: null }));
    once(`${card.id}|${specKey}|${runKey}`, () => runRef.current(card.id))
      .then((result) => { if (alive) setState({ loading: false, result, error: null }); })
      .catch((e) => { if (alive) setState({ loading: false, result: null, error: errorText(e) }); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card.id, specKey, runKey, seen]);

  const title = card.viz?.title || '';
  // A text card without its frame sits on the board like a heading.
  const plain = card.kind === 'text' && card.viz?.frame === 'plain';
  const frame = plain
    ? `rounded-lg border ${editing ? 'border-dashed border-[color:var(--border-default)]' : 'border-transparent'}`
    : 'rounded-lg border border-[color:var(--border-default)] bg-[color:var(--surface-base)]';
  // A text card's words are its content; its title is only a name for lists and Ask.
  const showTitle = card.kind !== 'text';
  const showHeader = editing || (card.kind !== 'text' && (Boolean(title) || Boolean(onAsk)));
  return (
    <div ref={rootRef} className={`group flex h-full flex-col overflow-hidden ${frame}`} onDoubleClick={onEditText || undefined}>
      <div className={`flex shrink-0 items-start justify-between gap-2 px-3 ${showHeader ? 'pt-2.5' : 'h-0 overflow-hidden'}`}>
        {renaming && onRename ? (
          // eslint-disable-next-line jsx-a11y/no-autofocus
          <input autoFocus defaultValue={title} aria-label="Card title" maxLength={120} placeholder="Untitled"
            onFocus={(e) => e.target.select()} onBlur={(e) => onRename(e.target.value)}
            onMouseDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') onRename(title); }}
            className="min-w-0 flex-1 rounded border border-[color:var(--brand-primary)] bg-surface px-1.5 py-0.5 text-[12px] font-medium text-[color:var(--text-primary)] outline-none" />
        ) : !showTitle ? <span /> : onStartRename ? (
          <button type="button" onClick={onStartRename} title="Rename"
            className={`min-w-0 truncate rounded px-1 -mx-1 text-left text-[12px] font-medium hover:bg-surface-hover ${title ? 'text-[color:var(--text-primary)]' : 'text-[color:var(--text-muted)]'}`}>
            {title || 'Untitled'}
          </button>
        ) : (
          <div className="min-w-0 truncate text-[12px] font-medium text-[color:var(--text-primary)]" title={title}>{title}</div>
        )}
        {!editing && onAsk && card.kind !== 'text' ? (
          <button type="button" aria-label={`Ask Lucid about ${title || 'this card'}`} title="Ask Lucid about this" onClick={() => onAsk(card)}
            className="shrink-0 rounded p-1 text-[color:var(--text-muted)] opacity-0 transition-opacity hover:bg-surface-hover hover:text-[color:var(--text-primary)] focus:opacity-100 group-hover:opacity-100">
            <AskIcon size={14} />
          </button>
        ) : null}
        {editing ? (
          <div className="flex shrink-0 items-center gap-0.5">
            <button type="button" aria-label={`Options for ${title || 'card'}`} onClick={(e) => onMenu && onMenu(card, e.currentTarget)}
              className="rounded p-1 text-[color:var(--text-muted)] transition-colors hover:bg-surface-hover hover:text-[color:var(--text-primary)]">
              <MoreHorizontal className="h-3.5 w-3.5" />
            </button>
            <span className="dash-card-drag cursor-grab rounded p-1 text-[color:var(--text-muted)] transition-colors hover:bg-surface-hover active:cursor-grabbing" aria-label="Drag to move">
              <GripVertical className="h-3.5 w-3.5" />
            </span>
          </div>
        ) : null}
      </div>
      <div className={`min-h-0 flex-1 px-3 pb-2.5 ${showHeader ? 'pt-1' : 'pt-2.5'}`}>
        {state.error ? <Center error>{state.error}</Center>
          : state.loading && !state.result ? <Center><BrandLoader size="sm" /></Center>
            : state.result ? <CardBody result={state.result} /> : null}
      </div>
    </div>
  );
}
